/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Typography, Input, Space, Row, Col, Drawer, Layout, Checkbox, message } from 'antd';
import { MessageOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button } from '@crc-evans/ui-components';
import { FileText, PenLine, Save, X } from 'lucide-react';
import DSRSignaturePad from '../DSR/DSRSignaturePad';
import axiosConfig from '../../services/axiosConfig';
import { getJwtToken } from '../../services/auth';
import moment from 'moment';
import { useSelector } from 'react-redux';

const { Title } = Typography;
const { TextArea } = Input;
const { Content, Footer } = Layout;

const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginBottom: 'var(--modern-spacing-lg, 24px)' }}>
    <h4 style={{
      margin: '0 0 12px 0',
      fontSize: 'var(--modern-font-size-base, 14px)',
      fontWeight: 500,
      color: 'var(--modern-text-primary, #374151)',
      textAlign: 'left'
    }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={8}
      style={{
        width: '100%',
        padding: '12px',
        fontSize: 'var(--modern-font-size-base, 14px)',
        border: '1px solid var(--modern-border-color, #E5E7EB)',
        borderRadius: 'var(--modern-border-radius, 8px)',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: 'var(--modern-text-primary, #1F2937)',
        backgroundColor: '#FFFFFF',
        lineHeight: '20px'
      }}
    />
  </div>
);

const CommentsLog = ({
  commentsData: initialCommentsData = {},
  onSave,
  onCancel,
  onGenerateReport,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  dprStatus = null,
  skipDayShift = false,
  skipNightShift = false,
  skipDpr = false,
  sowId
}) => {
  // Get skip states from Redux for the current sowId
  const sectionSkips = useSelector(state => {
    if (!sowId) {
      return {
        safety: false,
        quality: false,
        productivity: false,
        personnel: false,
        consumables: false,
        downtime: false
      };
    }
    const skipStates = state.dprReducer?.skipStatesBySection?.[sowId] || {};
    return {
      safety: skipStates.skipSafety || false,
      quality: skipStates.skipQuality || false,
      productivity: skipStates.skipProductivity || false,
      personnel: skipStates.skipPersonnel || false,
      consumables: skipStates.skipConsumables || false,
      downtime: skipStates.skipDowntime || false
    };
  });
  const [remarks, setRemarks] = useState('');
  const [managerName, setManagerName] = useState('');
  const [clientName, setClientName] = useState('');
  const [approverName, setApproverName] = useState('');
  const [signatureData, setSignatureData] = useState({});
  const [signatureTimestamps, setSignatureTimestamps] = useState({});
  const [signatureDrawerOpen, setSignatureDrawerOpen] = useState(false);
  const [signatureType, setSignatureType] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isGeneratingFlashReport, setIsGeneratingFlashReport] = useState(false);

  // Email modal state variables
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [isSharing, setIsSharing] = useState(false);

  // Flash report email modal state variables
  const [isFlashReportEmailModalVisible, setIsFlashReportEmailModalVisible] = useState(false);
  const [flashReportEmailInput, setFlashReportEmailInput] = useState('');
  const [flashReportEmailList, setFlashReportEmailList] = useState([]);
  const [isSharingFlashReport, setIsSharingFlashReport] = useState(false);
  const [skipWithoutEmailFlash, setSkipWithoutEmailFlash] = useState(false);

  // One-page PDF report email modal (same flow as Generate Report)
  const [isOnePageReportEmailModalVisible, setIsOnePageReportEmailModalVisible] = useState(false);
  const [onePageReportEmailInput, setOnePageReportEmailInput] = useState('');
  const [onePageReportEmailList, setOnePageReportEmailList] = useState([]);
  const [isSharingOnePageReport, setIsSharingOnePageReport] = useState(false);
  const [skipWithoutEmailOnePage, setSkipWithoutEmailOnePage] = useState(false);

  // Regular report email modal checkbox state
  const [skipWithoutEmail, setSkipWithoutEmail] = useState(false);

  // Update local state when prop changes
  useEffect(() => {
    // Set isSubmitted flag
    if (dprStatus === 'submitted') {
      setIsSubmitted(true);
    } else {
      setIsSubmitted(false);
    }

    // Prepopulate data if it exists (regardless of status)
    const hasData = initialCommentsData && Object.keys(initialCommentsData).length > 0;

    if (hasData) {
      // Set remarks based on current shift (day vs night comments)
      if (initialCommentsData.dprDayShiftComments !== undefined || initialCommentsData.dprNightShiftComments !== undefined) {
        const remarksToSet = shift === 'day'
          ? (initialCommentsData.dprDayShiftComments || '')
          : (initialCommentsData.dprNightShiftComments || '');
        setRemarks(remarksToSet);
      }

      // Names and signatures are SHARED across both day and night shifts
      if (initialCommentsData.managerName !== undefined) {
        setManagerName(initialCommentsData.managerName || '');
      }
      if (initialCommentsData.clientName !== undefined) {
        setClientName(initialCommentsData.clientName || '');
      }
      if (initialCommentsData.approverName !== undefined) {
        setApproverName(initialCommentsData.approverName || '');
      }
      if (initialCommentsData.managerSignature !== undefined && initialCommentsData.managerSignature) {
        setSignatureData(prev => ({ ...prev, manager: initialCommentsData.managerSignature }));
      }
      if (initialCommentsData.clientSignature !== undefined && initialCommentsData.clientSignature) {
        setSignatureData(prev => ({ ...prev, client: initialCommentsData.clientSignature }));
      }
      if (initialCommentsData.approverSignature !== undefined && initialCommentsData.approverSignature) {
        setSignatureData(prev => ({ ...prev, approver: initialCommentsData.approverSignature }));
      }
      if (initialCommentsData.managerSignatureTimestamp) {
        setSignatureTimestamps(prev => ({ ...prev, manager: initialCommentsData.managerSignatureTimestamp }));
      }
      if (initialCommentsData.clientSignatureTimestamp) {
        setSignatureTimestamps(prev => ({ ...prev, client: initialCommentsData.clientSignatureTimestamp }));
      }
      if (initialCommentsData.approverSignatureTimestamp) {
        setSignatureTimestamps(prev => ({ ...prev, approver: initialCommentsData.approverSignatureTimestamp }));
      }
    } else {
      // Reset to empty if no data
      setRemarks('');
      setManagerName('');
      setClientName('');
      setSignatureData({});
      setSignatureTimestamps({});
      setApproverName('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dprLogId,
    shift,
    initialCommentsData?.dprDayShiftComments,
    initialCommentsData?.dprNightShiftComments,
    initialCommentsData?.managerName,
    initialCommentsData?.clientName,
    initialCommentsData?.approverName,
    initialCommentsData?.managerSignature,
    initialCommentsData?.clientSignature,
    initialCommentsData?.approverSignature,
    initialCommentsData?.managerSignatureTimestamp,
    initialCommentsData?.clientSignatureTimestamp,
    initialCommentsData?.approverSignatureTimestamp
  ]);

  // Update remarks when shift changes for submitted DPRs
  useEffect(() => {
    if (dprStatus === 'submitted' && initialCommentsData) {
      const remarksToSet = shift === 'day'
        ? (initialCommentsData.dprDayShiftComments || '')
        : (initialCommentsData.dprNightShiftComments || '');
      setRemarks(remarksToSet);
    }
  }, [shift, dprStatus, initialCommentsData]);

  const openSignaturePad = (type) => {
    const validTypes = ['manager', 'client', 'approver'];
    if (typeof type !== 'string' || !validTypes.includes(type)) return;
    setSignatureType(type);
    setSignatureDrawerOpen(true);
  };

  const closeSignaturePad = () => {
    setSignatureDrawerOpen(false);
    setSignatureType(null);
  };

  const handleSignatureSave = (base64Image) => {
    if (signatureType) {
      if (base64Image) {
        setSignatureData(prev => ({
          ...prev,
          [signatureType]: base64Image
        }));
        setSignatureTimestamps(prev => ({
          ...prev,
          [signatureType]: moment().toISOString()
        }));
        // Close drawer after state update so saved signature shows in the form
        closeSignaturePad();
      } else {
        setSignatureData(prev => {
          const updated = { ...prev };
          delete updated[signatureType];
          return updated;
        });
        setSignatureTimestamps(prev => {
          const updated = { ...prev };
          delete updated[signatureType];
          return updated;
        });
        closeSignaturePad();
      }
    }
  };

  // Build DPR payload according to API contract (skip options, shift, section skips, comments for both shifts)
  const buildDprPayload = ({
    dprLogId,
    shift,
    dprDayShiftComments,
    dprNightShiftComments,
    skipDayShift,
    skipNightShift,
    skipDpr,
    sectionSkips,
    managerName,
    clientName,
    managerSignature,
    clientSignature,
    signatureTimestamps,
    status
  }) => {
    const isDay = shift === 'day';

    return {
      dprLog: {
        dprLogId: dprLogId,
        status: status || 'draft',
        dprDayShiftComments: dprDayShiftComments ?? '',
        dprNightShiftComments: dprNightShiftComments ?? '',

        isSkippedDayShift: skipDayShift ? 1 : 0,
        isSkippedNightShift: skipNightShift ? 1 : 0,
        isSkippedDpr: skipDpr ? 1 : 0,

        isSafetySkippedDayShift: isDay ? (sectionSkips.safety ? 1 : 0) : 0,
        isSafetySkippedNightShift: !isDay ? (sectionSkips.safety ? 1 : 0) : 0,

        isQualitySkippedDayShift: isDay ? (sectionSkips.quality ? 1 : 0) : 0,
        isQualitySkippedNightShift: !isDay ? (sectionSkips.quality ? 1 : 0) : 0,

        isProductivitySkippedDayShift: isDay ? (sectionSkips.productivity ? 1 : 0) : 0,
        isProductivitySkippedNightShift: !isDay ? (sectionSkips.productivity ? 1 : 0) : 0,

        isPersonnelSkippedDayShift: isDay ? (sectionSkips.personnel ? 1 : 0) : 0,
        isPersonnelSkippedNightShift: !isDay ? (sectionSkips.personnel ? 1 : 0) : 0,

        isConsumablesSkippedDayShift: isDay ? (sectionSkips.consumables ? 1 : 0) : 0,
        isConsumablesSkippedNightShift: !isDay ? (sectionSkips.consumables ? 1 : 0) : 0,

        isDowntimeSkippedDayShift: isDay ? (sectionSkips.downtime ? 1 : 0) : 0,
        isDowntimeSkippedNightShift: !isDay ? (sectionSkips.downtime ? 1 : 0) : 0,

        approvedBy1: managerSignature && managerName ? {
          teamMemberName: managerName,
          roleName: "Manager",
          signature: managerSignature.includes(',') ? managerSignature.split(',')[1] : managerSignature,
          signedAt: signatureTimestamps.manager || moment().toISOString()
        } : null,
        approvedBy2: clientSignature && clientName ? {
          teamMemberName: clientName,
          roleName: "Client",
          signature: clientSignature.includes(',') ? clientSignature.split(',')[1] : clientSignature,
          signedAt: signatureTimestamps.client || moment().toISOString()
        } : null,
        approvedBy3: signatureData.approver && approverName ? {
          teamMemberName: approverName,
          roleName: "Approver",
          signature: signatureData.approver.includes(',') ? signatureData.approver.split(',')[1] : signatureData.approver,
          signedAt: signatureTimestamps.approver || moment().toISOString()
        } : null
      }
    };
  };

  // Check if mandatory fields are filled
  const isFormValid = () => {
    return managerName.trim() !== '' && signatureData.manager;
  };

  const handleSave = async () => {
    if (!dprLogId) {
      message.error('DPR Log ID is required to save');
      return;
    }

    setIsSaving(true);
    try {
      const dprDayShiftComments = shift === 'day' ? remarks : (initialCommentsData.dprDayShiftComments ?? '');
      const dprNightShiftComments = shift === 'night' ? remarks : (initialCommentsData.dprNightShiftComments ?? '');
      const payload = buildDprPayload({
        dprLogId,
        shift,
        dprDayShiftComments,
        dprNightShiftComments,
        skipDayShift,
        skipNightShift,
        skipDpr,
        sectionSkips: sectionSkips,
        managerName,
        clientName,
        managerSignature: signatureData.manager,
        clientSignature: signatureData.client,
        signatureTimestamps,
        status: 'draft'
      });

      console.log('Saving DPR log with payload:', payload);

      const response = await axiosConfig.put(`/dpr-log/${dprLogId}`, payload);
      const responseData = response?.data?.data || response?.data;

      message.success({
        content: 'DPR saved successfully!',
        duration: 3
      });

      setIsSubmitted(false);

      // Prepopulate comment box from response if available
      if (responseData) {
        const updatedComments = shift === 'day'
          ? (responseData.dprDayShiftComments ?? remarks)
          : (responseData.dprNightShiftComments ?? remarks);
        if (updatedComments !== undefined) {
          setRemarks(updatedComments);
        }
      }

      if (onSave) {
        onSave({
          remarks,
          managerName,
          clientName,
          managerSignature: signatureData.manager,
          clientSignature: signatureData.client,
          skipSections: sectionSkips,
          responseData
        });
      }
    } catch (error) {
      console.error('Error saving DPR:', error);
      message.error({
        content: 'Failed to save DPR. Please try again.',
        duration: 3
      });
      setIsSubmitted(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!dprLogId) {
      message.error('DPR Log ID is required to submit');
      return;
    }

    if (!isFormValid()) {
      message.warning('Please add the mandatory signature before submitting.');
      return;
    }

    setIsSubmittingComment(true);
    try {
      const dprDayShiftComments = shift === 'day' ? remarks : (initialCommentsData.dprDayShiftComments ?? '');
      const dprNightShiftComments = shift === 'night' ? remarks : (initialCommentsData.dprNightShiftComments ?? '');
      const payload = buildDprPayload({
        dprLogId,
        shift,
        dprDayShiftComments,
        dprNightShiftComments,
        skipDayShift,
        skipNightShift,
        skipDpr,
        sectionSkips: sectionSkips,
        managerName,
        clientName,
        managerSignature: signatureData.manager,
        clientSignature: signatureData.client,
        signatureTimestamps,
        status: 'submitted'
      });

      console.log('Submitting DPR log with payload:', payload);

      const response = await axiosConfig.put(`/dpr-log/${dprLogId}`, payload);
      const responseData = response?.data?.data || response?.data;

      message.success({
        content: 'DPR submitted successfully!',
        duration: 3
      });

      setIsSubmitted(true);

      // Prepopulate comment box from response if available
      if (responseData) {
        const updatedComments = shift === 'day'
          ? (responseData.dprDayShiftComments ?? remarks)
          : (responseData.dprNightShiftComments ?? remarks);
        if (updatedComments !== undefined) {
          setRemarks(updatedComments);
        }
      }

      if (onSave) {
        onSave({
          remarks,
          managerName,
          clientName,
          managerSignature: signatureData.manager,
          clientSignature: signatureData.client,
          skipSections: sectionSkips,
          responseData
        });
      }
    } catch (error) {
      console.error('Error submitting DPR:', error);
      message.error({
        content: 'Failed to submit DPR. Please try again.',
        duration: 3
      });
      setIsSubmitted(false);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Email modal functions
  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
  };

  const handleAddEmail = () => {
    const emails = emailInput.split(',').map(e => e.trim()).filter(e => e);
    setEmailList(prev => [...new Set([...prev, ...emails])]);
    setEmailInput('');
  };

  const handleRemoveEmail = (email) => {
    setEmailList(prev => prev.filter(e => e !== email));
  };

  const handleEmailModalOk = async () => {
    if (!skipWithoutEmail && emailList.length === 0) {
      message.warning('Please add at least one email address or check "Skip without adding new email".');
      return;
    }

    if (!dprLogId) {
      message.error('DPR Log ID is required to generate report');
      return;
    }

    setIsSharing(true);
    try {
      let url = `${process.env.REACT_APP_BASE_URL}/dpr-log/generate-pdf-report?dprLogId=${dprLogId}`;

      // Only add emailToDestinations if checkbox is not checked
      if (!skipWithoutEmail && emailList.length > 0) {
        const emailToDestinations = encodeURIComponent(emailList.join(','));
        url += `&emailToDestinations=${emailToDestinations}`;
      }

      // Get authentication token
      const authToken = await getJwtToken();

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate and share report');
      }

      message.success('Report generated and shared successfully!');
      setIsEmailModalVisible(false);
      setEmailList([]);
      setEmailInput('');
      setSkipWithoutEmail(false);
    } catch (err) {
      console.error('Error sharing report:', err);
      message.error('Failed to share report. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleEmailModalCancel = () => {
    setIsEmailModalVisible(false);
    setEmailList([]);
    setEmailInput('');
    setSkipWithoutEmail(false);
  };

  // Generate Flash Report handler - opens email modal
  const handleGenerateFlashReport = () => {
    if (!dprLogId) {
      message.error('DPR Log ID is required to generate flash report');
      return;
    }
    // Open flash report email modal
    setIsFlashReportEmailModalVisible(true);
  };

  // Flash report email modal functions
  const handleFlashReportEmailInputChange = (e) => {
    setFlashReportEmailInput(e.target.value);
  };

  const handleAddFlashReportEmail = () => {
    const emails = flashReportEmailInput.split(',').map(e => e.trim()).filter(e => e);
    setFlashReportEmailList(prev => [...new Set([...prev, ...emails])]);
    setFlashReportEmailInput('');
  };

  const handleRemoveFlashReportEmail = (email) => {
    setFlashReportEmailList(prev => prev.filter(e => e !== email));
  };

  const handleFlashReportEmailModalOk = async () => {
    if (!skipWithoutEmailFlash && flashReportEmailList.length === 0) {
      message.warning('Please add at least one email address or check "Skip without adding new email".');
      return;
    }

    if (!dprLogId) {
      message.error('DPR Log ID is required to generate flash report');
      return;
    }

    setIsSharingFlashReport(true);
    try {
      let url = `${process.env.REACT_APP_BASE_URL}/dpr-log/generate-flash-report?dprLogId=${dprLogId}&shift=${shift}`;

      // Only add emailToDestinations if checkbox is not checked
      if (!skipWithoutEmailFlash && flashReportEmailList.length > 0) {
        const emailToDestinations = encodeURIComponent(flashReportEmailList.join(','));
        url += `&emailToDestinations=${emailToDestinations}`;
      }

      // Get authentication token
      const authToken = await getJwtToken();

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate and share flash report');
      }

      message.success('Flash report generated and shared successfully!');
      setIsFlashReportEmailModalVisible(false);
      setFlashReportEmailList([]);
      setFlashReportEmailInput('');
      setSkipWithoutEmailFlash(false);
    } catch (err) {
      console.error('Error sharing flash report:', err);
      message.error('Failed to share flash report. Please try again.');
    } finally {
      setIsSharingFlashReport(false);
    }
  };

  const handleFlashReportEmailModalCancel = () => {
    setIsFlashReportEmailModalVisible(false);
    setFlashReportEmailList([]);
    setFlashReportEmailInput('');
    setSkipWithoutEmailFlash(false);
  };

  const handleGenerateOnePageReport = () => {
    if (!dprLogId) {
      message.error('DPR Log ID is required to generate one page report');
      return;
    }
    setIsOnePageReportEmailModalVisible(true);
  };

  const handleOnePageReportEmailInputChange = (e) => {
    setOnePageReportEmailInput(e.target.value);
  };

  const handleAddOnePageReportEmail = () => {
    const emails = onePageReportEmailInput.split(',').map((e) => e.trim()).filter((e) => e);
    setOnePageReportEmailList((prev) => [...new Set([...prev, ...emails])]);
    setOnePageReportEmailInput('');
  };

  const handleRemoveOnePageReportEmail = (email) => {
    setOnePageReportEmailList((prev) => prev.filter((e) => e !== email));
  };

  const handleOnePageReportEmailModalOk = async () => {
    if (!skipWithoutEmailOnePage && onePageReportEmailList.length === 0) {
      message.warning('Please add at least one email address or check "Skip without adding new email".');
      return;
    }

    if (!dprLogId) {
      message.error('DPR Log ID is required to generate one page report');
      return;
    }

    setIsSharingOnePageReport(true);
    try {
      let url = `${process.env.REACT_APP_BASE_URL}/dpr-log/generate-one-page-pdf-report?dprLogId=${dprLogId}`;

      if (!skipWithoutEmailOnePage && onePageReportEmailList.length > 0) {
        const emailToDestinations = encodeURIComponent(onePageReportEmailList.join(','));
        url += `&emailToDestinations=${emailToDestinations}`;
      }

      const authToken = await getJwtToken();

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate and share one page report');
      }

      message.success('One page report generated and shared successfully!');
      setIsOnePageReportEmailModalVisible(false);
      setOnePageReportEmailList([]);
      setOnePageReportEmailInput('');
      setSkipWithoutEmailOnePage(false);
    } catch (err) {
      console.error('Error sharing one page report:', err);
      message.error('Failed to share one page report. Please try again.');
    } finally {
      setIsSharingOnePageReport(false);
    }
  };

  const handleOnePageReportEmailModalCancel = () => {
    setIsOnePageReportEmailModalVisible(false);
    setOnePageReportEmailList([]);
    setOnePageReportEmailInput('');
    setSkipWithoutEmailOnePage(false);
  };

  const handleGenerateReport = () => {
    // Open email modal instead of calling callback
    setIsEmailModalVisible(true);
  };

  return (
    <div style={{
      padding: 0,
      minHeight: '100vh',
      backgroundColor: 'var(--modern-bg-content, #F8FAFC)',
      textAlign: 'left'
    }}>
      {/* Main Content Card */}
      <div style={{
        backgroundColor: 'var(--modern-bg-primary, #FFFFFF)',
        borderRadius: 'var(--modern-border-radius, 12px)',
        padding: 'var(--modern-spacing-xl, 32px)',
        boxShadow: 'var(--modern-shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.1))',
        maxWidth: '100%',
        textAlign: 'left'
      }}>
        {/* Header with Icon */}
        <div style={{ marginBottom: 'var(--modern-spacing-lg, 24px)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: 'var(--modern-border-radius, 8px)',
              backgroundColor: 'var(--modern-bg-secondary, #F3F4F6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <MessageOutlined style={{ color: 'var(--modern-text-secondary, #4B5563)', fontSize: '20px' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{
                margin: '0 0 4px 0',
                color: 'var(--modern-text-primary, #111827)',
                fontSize: 'var(--modern-font-size-xl, 20px)',
                fontWeight: 600,
                lineHeight: '28px',
                textAlign: 'left'
              }}>
                Overall Comments
              </h2>
              <p style={{
                margin: 0,
                color: 'var(--modern-text-secondary, #6B7280)',
                fontSize: 'var(--modern-font-size-base, 14px)',
                lineHeight: '20px',
                textAlign: 'left'
              }}>
                Add any general remarks or overall feedback for the report.
              </p>
            </div>
          </div>
        </div>

        {/* CommentBox */}
        <CommentBox
          header="Notes / Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Enter overall comments here..."
        />

        {/* Manager Name, Client Name, Approver Name Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: 'var(--modern-spacing-lg, 24px)'
        }}>
          <div style={{ minWidth: 0 }}>
            <label style={{
              display: 'block',
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              Manager Name <span style={{ color: '#ff4d4f' }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Manager Name"
              value={managerName}
              onChange={(e) => setManagerName(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                borderRadius: 'var(--modern-border-radius, 8px)',
                fontSize: 'var(--modern-font-size-base, 14px)',
                height: '40px',
                backgroundColor: '#FFFFFF',
                border: '1px solid var(--modern-border-color, #E5E7EB)',
                color: 'var(--modern-text-primary, #1F2937)',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{
              display: 'block',
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              Client Name
            </label>
            <input
              type="text"
              placeholder="Client Name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                borderRadius: 'var(--modern-border-radius, 8px)',
                fontSize: 'var(--modern-font-size-base, 14px)',
                height: '40px',
                backgroundColor: '#FFFFFF',
                border: '1px solid var(--modern-border-color, #E5E7EB)',
                color: 'var(--modern-text-primary, #1F2937)',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{
              display: 'block',
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              Approver Name
            </label>
            <input
              type="text"
              placeholder="Approver Name"
              value={approverName}
              onChange={(e) => setApproverName(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                borderRadius: 'var(--modern-border-radius, 8px)',
                fontSize: 'var(--modern-font-size-base, 14px)',
                height: '40px',
                backgroundColor: '#FFFFFF',
                border: '1px solid var(--modern-border-color, #E5E7EB)',
                color: 'var(--modern-text-primary, #1F2937)',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>

        {/* Manager Signature, Client Signature, Approver Signature Section */}
        <Row gutter={16} style={{ marginBottom: 'var(--modern-spacing-lg, 24px)' }}>
          <Col xs={24} sm={8}>
            <div style={{
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              Manager Signature <span style={{ color: '#ff4d4f' }}>*</span>
            </div>
            {!signatureData.manager ? (
              <button
                type="button"
                onClick={() => openSignaturePad('manager')}
                style={{
                  height: '60px',
                  width: '100%',
                  border: '1px dashed var(--modern-border-color, #E5E7EB)',
                  borderRadius: 'var(--modern-border-radius, 8px)',
                  backgroundColor: '#FFFFFF',
                  color: 'var(--modern-text-secondary, #6B7280)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: 'var(--modern-font-size-base, 14px)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                <PenLine size={18} strokeWidth={2} />
                Add Signature
              </button>
            ) : (
              <div
                style={{
                  cursor: 'pointer',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '8px',
                  backgroundColor: '#FFFFFF',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onClick={() => openSignaturePad('manager')}
              >
                <img
                  src={signatureData.manager}
                  alt="Manager Signature"
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '120px',
                    objectFit: 'contain'
                  }}
                />
                {signatureTimestamps.manager && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center'
                  }}>
                    Signed: {moment(signatureTimestamps.manager).format('DD/MM/YYYY hh:mm A')}
                  </div>
                )}
              </div>
            )}
          </Col>
          <Col xs={24} sm={8}>
            <div style={{
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              Client Signature
            </div>
            {!signatureData.client ? (
              <button
                type="button"
                onClick={() => openSignaturePad('client')}
                style={{
                  height: '60px',
                  width: '100%',
                  border: '1px dashed var(--modern-border-color, #E5E7EB)',
                  borderRadius: 'var(--modern-border-radius, 8px)',
                  backgroundColor: '#FFFFFF',
                  color: 'var(--modern-text-secondary, #6B7280)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: 'var(--modern-font-size-base, 14px)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                <PenLine size={18} strokeWidth={2} />
                Add Signature
              </button>
            ) : (
              <div
                style={{
                  cursor: 'pointer',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '8px',
                  backgroundColor: '#FFFFFF',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onClick={() => openSignaturePad('client')}
              >
                <img
                  src={signatureData.client}
                  alt="Client Signature"
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '120px',
                    objectFit: 'contain'
                  }}
                />
                {signatureTimestamps.client && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center'
                  }}>
                    Signed: {moment(signatureTimestamps.client).format('DD/MM/YYYY hh:mm A')}
                  </div>
                )}
              </div>
            )}
          </Col>
          <Col xs={24} sm={8}>
            <div style={{
              color: 'var(--modern-text-primary, #374151)',
              fontSize: 'var(--modern-font-size-base, 14px)',
              fontWeight: 500,
              marginBottom: '8px',
              textAlign: 'left'
            }}>
              Approver Signature
            </div>
            {!signatureData.approver ? (
              <button
                type="button"
                onClick={() => openSignaturePad('approver')}
                style={{
                  height: '60px',
                  width: '100%',
                  border: '1px dashed var(--modern-border-color, #E5E7EB)',
                  borderRadius: 'var(--modern-border-radius, 8px)',
                  backgroundColor: '#FFFFFF',
                  color: 'var(--modern-text-secondary, #6B7280)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: 'var(--modern-font-size-base, 14px)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                <PenLine size={18} strokeWidth={2} />
                Add Signature
              </button>
            ) : (
              <div
                style={{
                  cursor: 'pointer',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '8px',
                  backgroundColor: '#FFFFFF',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
                onClick={() => openSignaturePad('approver')}
              >
                <img
                  src={signatureData.approver}
                  alt="Approver Signature"
                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '120px',
                    objectFit: 'contain'
                  }}
                />
                {signatureTimestamps.approver && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6B7280',
                    textAlign: 'center'
                  }}>
                    Signed: {moment(signatureTimestamps.approver).format('DD/MM/YYYY hh:mm A')}
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>

        {/* Action Buttons - Save/Submit use native button so disabled works (ui-components Button does not support disabled) */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '12px', marginTop: '32px' }}>
          <Button
            label="Cancel"
            onClick={handleCancel}
            variant="tertiary"
            size="medium"
            style={{
              backgroundColor: 'transparent',
              borderColor: 'var(--modern-border-color, #D1D5DB)',
              color: 'var(--modern-text-primary, #374151)'
            }}
          />
          <Button
            label={isSaving ? 'Saving...' : 'Save'}
            onClick={handleSave}
            disabled={!dprLogId || isSaving}
            variant="secondary"
            icon={isSaving ? LoadingOutlined : Save}
            size="medium"
            style={{
              backgroundColor: (!dprLogId || isSaving) ? '#E5E7EB' : '#0F172A',
              borderColor: (!dprLogId || isSaving) ? '#E5E7EB' : '#0F172A',
              color: '#FFFFFF',
              opacity: (!dprLogId || isSaving) ? 0.7 : 1,
              cursor: (!dprLogId || isSaving) ? 'not-allowed' : 'pointer'
            }}
          />
          <Button
            label={isSubmittingComment ? 'Submitting...' : 'Submit'}
            onClick={handleSubmit}
            disabled={dprStatus === 'submitted' || !isFormValid() || isSubmittingComment}
            variant="secondary"
            icon={isSubmittingComment ? LoadingOutlined : null}
            size="medium"
            style={{
              backgroundColor: (dprStatus === 'submitted' || !isFormValid() || isSubmittingComment) ? '#FEE2E2' : '#991B1B',
              borderColor: (dprStatus === 'submitted' || !isFormValid() || isSubmittingComment) ? '#FEE2E2' : '#991B1B',
              color: '#FFFFFF',
              opacity: (dprStatus === 'submitted' || !isFormValid() || isSubmittingComment) ? 0.8 : 1,
              cursor: (dprStatus === 'submitted' || !isFormValid() || isSubmittingComment) ? 'not-allowed' : 'pointer'
            }}
          />
          <Button
            label="Generate Flash Report"
            onClick={handleGenerateFlashReport}
            icon={FileText}
            variant="secondary"
            size="medium"
            style={{
              backgroundColor: '#EF4444',
              borderColor: '#EF4444',
              color: '#FFFFFF'
            }}
          />
          <Button
            label="Generate Report"
            onClick={handleGenerateReport}
            disabled={!isSubmitted && dprStatus !== 'submitted'}
            variant="secondary"
            icon={FileText}
            size="medium"
            style={{
              backgroundColor: (!isSubmitted && dprStatus !== 'submitted') ? '#FEE2E2' : '#991B1B',
              borderColor: (!isSubmitted && dprStatus !== 'submitted') ? '#FEE2E2' : '#991B1B',
              color: '#FFFFFF',
              opacity: (!isSubmitted && dprStatus !== 'submitted') ? 0.8 : 1,
              cursor: (!isSubmitted && dprStatus !== 'submitted') ? 'not-allowed' : 'pointer'
            }}
          />
          <Button
            label="One Page Report"
            onClick={handleGenerateOnePageReport}
            disabled={!isSubmitted && dprStatus !== 'submitted'}
            variant="secondary"
            icon={FileText}
            size="medium"
            style={{
              backgroundColor: (!isSubmitted && dprStatus !== 'submitted') ? '#FEE2E2' : '#991B1B',
              borderColor: (!isSubmitted && dprStatus !== 'submitted') ? '#FEE2E2' : '#991B1B',
              color: '#FFFFFF',
              opacity: (!isSubmitted && dprStatus !== 'submitted') ? 0.8 : 1,
              cursor: (!isSubmitted && dprStatus !== 'submitted') ? 'not-allowed' : 'pointer'
            }}
          />
        </div>
      </div>

      <Drawer
        title={
          <span style={{
            color: 'var(--modern-text-primary, #111827)',
            fontSize: 'var(--modern-font-size-lg, 16px)',
            fontWeight: 600
          }}>
            {signatureType === 'manager' ? 'Manager' : signatureType === 'client' ? 'Client' : 'Approver'} signature
          </span>
        }
        placement="right"
        onClose={closeSignaturePad}
        visible={signatureDrawerOpen}
        width={520}
        bodyStyle={{
          padding: '20px',
          backgroundColor: 'var(--modern-bg-content, #F8FAFC)',
          maxHeight: 'calc(100vh - 55px)',
          overflow: 'auto'
        }}
        headerStyle={{
          borderBottom: '1px solid var(--modern-border-color, #E5E7EB)',
          backgroundColor: 'var(--modern-bg-primary, #FFFFFF)',
          padding: '16px 24px'
        }}
      >
        <DSRSignaturePad
          visible={signatureDrawerOpen}
          onClose={closeSignaturePad}
          onSave={handleSignatureSave}
          title={signatureType === 'manager' ? 'Manager' : signatureType === 'client' ? 'Client' : 'Approver'}
          initialSignature={signatureType ? signatureData[signatureType] : null}
        />
      </Drawer>

      {/* Email Modal for Report Sharing */}
      {isEmailModalVisible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '100px',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            width: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{
                  backgroundColor: '#EEF2FF',
                  padding: '10px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#4F46E5'
                }}>
                  <FileText size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                    Generate Report
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                    Share the daily progress report via email
                  </p>
                </div>
              </div>
              <button
                onClick={handleEmailModalCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Email Addresses
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    placeholder="Enter emails followed by comma (,)"
                    value={emailInput}
                    onChange={handleEmailInputChange}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937'
                    }}
                  />
                  <Button
                    label="Add"
                    variant="primary"
                    onClick={handleAddEmail}
                    disabled={!emailInput.trim() || isSharing}
                    size="small"
                  />
                </div>
                <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#6B7280', textAlign: 'left' }}>
                  e.g., manager@example.com, client@example.com
                </p>

                {emailList.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #F3F4F6',
                    marginBottom: '16px'
                  }}>
                    {emailList.map(email => (
                      <div
                        key={email}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: 'full',
                          fontSize: '13px',
                          color: '#374151'
                        }}
                      >
                        {email}
                        <button
                          onClick={() => handleRemoveEmail(email)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: '#9CA3AF',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                  <input
                    type="checkbox"
                    id="skipEmptyEmail"
                    checked={skipWithoutEmail}
                    onChange={(e) => setSkipWithoutEmail(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="skipEmptyEmail" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                    Skip without adding new email
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#F9FAFB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #E5E7EB'
            }}>
              <Button
                label="Cancel"
                variant="tertiary"
                onClick={handleEmailModalCancel}
                size="medium"
              />
              <Button
                label={isSharing ? "Sharing..." : "Generate & Share"}
                variant="primary"
                onClick={handleEmailModalOk}
                disabled={isSharing || (!skipWithoutEmail && emailList.length === 0)}
                size="medium"
                icon={FileText}
              />
            </div>
          </div>
        </div>
      )}

      {/* Flash Report Email Modal */}
      {isFlashReportEmailModalVisible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '100px',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            width: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{
                  backgroundColor: '#FEF2F2', // Light Red for flash report
                  padding: '10px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#EF4444' // Red 500
                }}>
                  <FileText size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                    Generate Flash Report
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                    Share summary flash report via email
                  </p>
                </div>
              </div>
              <button
                onClick={handleFlashReportEmailModalCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Email Addresses
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    placeholder="Enter emails followed by comma (,)"
                    value={flashReportEmailInput}
                    onChange={handleFlashReportEmailInputChange}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddFlashReportEmail()}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937'
                    }}
                  />
                  <Button
                    label="Add"
                    variant="primary"
                    onClick={handleAddFlashReportEmail}
                    disabled={!flashReportEmailInput.trim() || isSharingFlashReport}
                    size="small"
                  />
                </div>
                <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#6B7280', textAlign: 'left' }}>
                  e.g., manager@example.com, client@example.com
                </p>

                {flashReportEmailList.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #F3F4F6',
                    marginBottom: '16px'
                  }}>
                    {flashReportEmailList.map(email => (
                      <div
                        key={email}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: 'full',
                          fontSize: '13px',
                          color: '#374151'
                        }}
                      >
                        {email}
                        <button
                          onClick={() => handleRemoveFlashReportEmail(email)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: '#9CA3AF',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                  <input
                    type="checkbox"
                    id="skipEmptyFlashEmail"
                    checked={skipWithoutEmailFlash}
                    onChange={(e) => setSkipWithoutEmailFlash(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="skipEmptyFlashEmail" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                    Skip without adding new email
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#F9FAFB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #E5E7EB'
            }}>
              <Button
                label="Cancel"
                variant="tertiary"
                onClick={handleFlashReportEmailModalCancel}
                size="medium"
              />
              <Button
                label={isSharingFlashReport ? "Sharing..." : "Generate & Share"}
                variant="primary"
                onClick={handleFlashReportEmailModalOk}
                disabled={isSharingFlashReport || (!skipWithoutEmailFlash && flashReportEmailList.length === 0)}
                size="medium"
                icon={FileText}
                style={{
                  backgroundColor: '#EF4444',
                  borderColor: '#EF4444'
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* One Page Report Email Modal */}
      {isOnePageReportEmailModalVisible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '100px',
          zIndex: 1000
        }}
        >
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            width: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
          >
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}
            >
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{
                  backgroundColor: '#EEF2FF',
                  padding: '10px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#4F46E5'
                }}
                >
                  <FileText size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                    One Page Report
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                    Share the condensed one-page DPR PDF via email
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleOnePageReportEmailModalCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}
                >
                  Email Addresses
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    placeholder="Enter emails followed by comma (,)"
                    value={onePageReportEmailInput}
                    onChange={handleOnePageReportEmailInputChange}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddOnePageReportEmail()}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937'
                    }}
                  />
                  <Button
                    label="Add"
                    variant="primary"
                    onClick={handleAddOnePageReportEmail}
                    disabled={!onePageReportEmailInput.trim() || isSharingOnePageReport}
                    size="small"
                  />
                </div>
                <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#6B7280', textAlign: 'left' }}>
                  e.g., manager@example.com, client@example.com
                </p>

                {onePageReportEmailList.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #F3F4F6',
                    marginBottom: '16px'
                  }}
                  >
                    {onePageReportEmailList.map((email) => (
                      <div
                        key={email}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: 'full',
                          fontSize: '13px',
                          color: '#374151'
                        }}
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveOnePageReportEmail(email)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: '#9CA3AF',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                  <input
                    type="checkbox"
                    id="skipEmptyOnePageEmail"
                    checked={skipWithoutEmailOnePage}
                    onChange={(e) => setSkipWithoutEmailOnePage(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <label htmlFor="skipEmptyOnePageEmail" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                    Skip without adding new email
                  </label>
                </div>
              </div>
            </div>

            <div style={{
              padding: '16px 24px',
              backgroundColor: '#F9FAFB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #E5E7EB'
            }}
            >
              <Button
                label="Cancel"
                variant="tertiary"
                onClick={handleOnePageReportEmailModalCancel}
                size="medium"
              />
              <Button
                label={isSharingOnePageReport ? 'Sharing...' : 'Generate & Share'}
                variant="primary"
                onClick={handleOnePageReportEmailModalOk}
                disabled={isSharingOnePageReport || (!skipWithoutEmailOnePage && onePageReportEmailList.length === 0)}
                size="medium"
                icon={FileText}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentsLog;
